import { useMemo } from 'react';

import { NoData } from '@revelio/replots';

import { TextChartContent } from '../Chart/TextChart/TextChartContent';
import type { TabContent } from '../PageConfigProvider/page-reducer';
import type { Page } from '../PageConfigProvider/types';
import {
  buildReportFlowDocument,
  nestedPagesFromTabContent,
} from '../ReportFlow/convert-grid-report-to-flow';
import type { ReportFlowLeafContentRenderer } from '../ReportFlow/ReportFlowBlockView';
import type { ReportFlowCoverParameter } from '../ReportFlow/ReportFlowCoverParameters';
import { ReportFlowDocumentView } from '../ReportFlow/ReportFlowDocumentView';
import type { ChartDataEntry } from './SharedReportDataContext';
import { StaticChartRenderer } from './StaticChartRenderer';
import styles from './shared-flow-report.module.css';

export const SHARED_FLOW_LAYOUT_MARKER = 'flow';

const createStaticLeafRenderer =
  (chartData: Record<string, ChartDataEntry>): ReportFlowLeafContentRenderer =>
  (leaf) => {
    if (leaf.kind === 'text') {
      return (
        <TextChartContent
          content={leaf.item.plot?.textHtml ?? ''}
          color={leaf.item.plot?.color}
          vAlign={leaf.item.plot?.textVAlign}
        />
      );
    }
    if (leaf.kind === 'image') {
      const imageSrc = leaf.item.plot?.imageSrc;
      return (
        <div className={styles.imageBody}>
          {imageSrc && <img className={styles.image} src={imageSrc} alt="" />}
        </div>
      );
    }
    const entry = chartData[leaf.item.id];
    return (
      <div className={styles.chartCard}>
        {leaf.item.title && (
          <p className={styles.chartTitle}>{leaf.item.title}</p>
        )}
        <div className={styles.chartBody}>
          {entry?.component ? (
            <StaticChartRenderer chartDataEntry={entry} />
          ) : (
            <div className={styles.noData}>
              <NoData />
            </div>
          )}
        </div>
      </div>
    );
  };

export const SharedFlowReport = ({
  page,
  tabContent,
  parameters,
  chartData,
}: {
  page: Page;
  tabContent: TabContent;
  parameters: ReportFlowCoverParameter[];
  chartData: Record<string, ChartDataEntry>;
}) => {
  const doc = useMemo(
    () =>
      buildReportFlowDocument({
        title: tabContent.title || page.name,
        description: tabContent.description,
        created: page.created,
        pages: nestedPagesFromTabContent(tabContent),
      }),
    [page, tabContent]
  );
  const renderLeafContent = useMemo(
    () => createStaticLeafRenderer(chartData),
    [chartData]
  );

  return (
    <div className={styles.page} data-testid="shared-flow-report">
      <ReportFlowDocumentView
        doc={doc}
        parameters={parameters}
        renderLeafContent={renderLeafContent}
      />
    </div>
  );
};
